import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { NipLookupButton } from "@/components/common/NipLookupButton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, Search, User, Building2, Paperclip, X, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

export function QuickActionDialogs({ openKey, setOpenKey, session }) {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [issuers, setIssuers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Guard against null session or user
  const user = session?.user;
  const canCreateClient = session?.role === "ADMIN" || session?.role === "MANAGER";
  const canCreateInvoice = session?.role === "ADMIN";

  // Auto-redirect invoice to full page
  useEffect(() => {
    if (openKey === "invoice") {
      nav("/faktury?new=true");
      setOpenKey("");
    }
  }, [openKey, nav, setOpenKey]);

  useEffect(() => {
    if (!openKey) return;
    setError("");

    // load reference data lazily
    let alive = true;
    (async () => {
      try {
        const [cRes, iRes, uRes] = await Promise.all([
          api.get("/clients"),
          canCreateInvoice ? api.get("/issuers") : Promise.resolve({ data: [] }),
          api.get("/users/list"),
        ]);
        if (!alive) return;
        setClients(cRes.data || []);
        setIssuers(iRes.data || []);
        setAllUsers(uRes.data?.users || []);
      } catch {
        // ignore
      }
    })();

    return () => {
      alive = false;
    };
  }, [openKey, canCreateInvoice]);

  const getUserDisplayName = (userId) => {
    const u = allUsers.find(u => u.id === userId);
    return u?.fullName || u?.email || userId;
  };

  const close = () => setOpenKey("");

  // -------- Create Client --------
  const [clientForm, setClientForm] = useState({
    name: "",
    nip: "",
    regon: "",
    primaryEmail: "",
    status: "ACTIVE",
    tags: "",
    phone: "",
    address: "",
    notes: "",
  });

  const submitClient = async () => {
    if (!canCreateClient) return;
    setLoading(true);
    setError("");
    try {
      const payload = {
        name: clientForm.name,
        nip: clientForm.nip,
        regon: clientForm.regon,
        primaryEmail: clientForm.primaryEmail,
        extraEmails: [],
        phone: clientForm.phone,
        address: clientForm.address,
        status: clientForm.status,
        ownerUserId: user?.id || "",
        tags: clientForm.tags
          ? clientForm.tags.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        notes: clientForm.notes,
        notesClassification: "CONFIDENTIAL",
        notesDlpFindings: [],
        invoiceProfile: {
          buyerName: clientForm.name,
          buyerNip: clientForm.nip,
          buyerAddress: clientForm.address,
          invoiceEmail: clientForm.primaryEmail,
          defaultPaymentDays: 14,
        },
        gdpr: {
          legalBasis: "CONTRACT",
          acquiredAt: new Date().toISOString(),
          relationshipEndedAt: null,
          processingRestricted: false,
          highRisk: false,
        },
      };
      const res = await api.post("/clients", payload);
      close();
      nav(`/klienci/${res.data.id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Nie udało się dodać klienta.");
    } finally {
      setLoading(false);
    }
  };

  // -------- Create Task --------
  const [taskForm, setTaskForm] = useState({
    clientId: "",
    title: "",
    description: "",
    assigneeUserId: "",
    assigneeUserIds: [],
    status: "TODO",
    priority: "MEDIUM",
    dueDate: "",
  });
  const [taskFiles, setTaskFiles] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    if (!taskForm.clientId || !openKey) return;
    let alive = true;
    api
      .get(`/clients/${taskForm.clientId}/team`)
      .then((res) => {
        if (!alive) return;
        const members = (res.data?.members || []).map((m) => m.userId);
        setTeamMembers(members);
        // Only auto-assign if assigneeUserId is empty (don't override user input)
        if (!taskForm.assigneeUserId) {
          if (members.includes(user?.id)) {
            setTaskForm((f) => ({ ...f, assigneeUserId: user?.id }));
          } else if (members.length) {
            setTaskForm((f) => ({ ...f, assigneeUserId: members[0] }));
          }
        }
      })
      .catch(() => {
        if (!alive) return;
        setTeamMembers([]);
      });
    return () => {
      alive = false;
    };
  }, [taskForm.clientId, openKey, user?.id]);

  const submitTask = async () => {
    if (!canCreateClient) return; // same roles as create task
    setLoading(true);
    setError("");
    try {
      const payload = {
        clientId: taskForm.clientId,
        title: taskForm.title,
        description: taskForm.description,
        descriptionClassification: "CONFIDENTIAL",
        descriptionDlpFindings: [],
        assigneeUserId: taskForm.assigneeUserIds[0] || taskForm.assigneeUserId,
        assigneeUserIds: taskForm.assigneeUserIds.length > 0 ? taskForm.assigneeUserIds : (taskForm.assigneeUserId ? [taskForm.assigneeUserId] : []),
        status: taskForm.status,
        priority: taskForm.priority,
        dueDate: taskForm.dueDate ? new Date(taskForm.dueDate).toISOString() : null,
      };
      const res = await api.post("/tasks", payload);
      // Upload pending attachments
      if (taskFiles.length > 0) {
        for (const file of taskFiles) {
          const formData = new FormData();
          formData.append("file", file);
          await api.post(`/tasks/${res.data.id}/attachments`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          }).catch(() => {});
        }
      }
      setTaskFiles([]);
      close();
      nav(`/zadania/${res.data.id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Nie udało się utworzyć zadania.");
    } finally {
      setLoading(false);
    }
  };

  // -------- Create Invoice --------
  const [invoiceForm, setInvoiceForm] = useState({
    issuerId: "",
    clientId: "",
    itemName: "Usługa księgowa",
    qty: 1,
    unitPriceNet: 1000,
    vatRate: 23,
    paymentDays: 14,
  });

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === invoiceForm.clientId),
    [clients, invoiceForm.clientId]
  );

  const submitInvoice = async () => {
    if (!canCreateInvoice) return;
    setLoading(true);
    setError("");
    try {
      const today = new Date();
      const due = new Date();
      due.setDate(due.getDate() + Number(invoiceForm.paymentDays || 14));

      const buyerName = selectedClient?.invoiceProfile?.buyerName || selectedClient?.name || "";
      const buyerAddress = selectedClient?.invoiceProfile?.buyerAddress || selectedClient?.address || "";
      const buyerEmail = selectedClient?.invoiceProfile?.invoiceEmail || selectedClient?.primaryEmail || "";
      const buyerNip = selectedClient?.invoiceProfile?.buyerNip || "";

      const payload = {
        issuerId: invoiceForm.issuerId,
        clientId: invoiceForm.clientId,
        issueDate: new Date(isoDate(today)).toISOString(),
        saleDate: new Date(isoDate(today)).toISOString(),
        dueDate: new Date(isoDate(due)).toISOString(),
        status: "DRAFT",
        number: "",
        buyerName,
        buyerNip,
        buyerAddress,
        buyerEmail,
        items: [
          {
            name: invoiceForm.itemName,
            qty: Number(invoiceForm.qty || 1),
            unitPriceNet: Number(invoiceForm.unitPriceNet || 0),
            vatRate: Number(invoiceForm.vatRate || 0),
          },
        ],
      };

      await api.post("/invoices", payload);
      close();
      nav("/faktury");
    } catch (e) {
      setError(e?.response?.data?.detail || "Nie udało się utworzyć faktury.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={openKey === "client"} onOpenChange={(o) => setOpenKey(o ? "client" : "")}> 
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quick-client-dialog">
          <DialogHeader>
            <DialogTitle data-testid="quick-client-title">Dodaj klienta</DialogTitle>
          </DialogHeader>
          {!canCreateClient ? (
            <Alert data-testid="quick-client-no-access">
              <AlertTitle>Brak dostępu</AlertTitle>
              <AlertDescription>Dodawanie klientów jest dostępne dla Administratora i Menedżera.</AlertDescription>
            </Alert>
          ) : null}
          {error ? (
            <Alert data-testid="quick-client-error" variant="destructive">
              <AlertTitle>Błąd</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="grid gap-3 md:grid-cols-2">
            <Input data-testid="quick-client-name" placeholder="Nazwa firmy / Imię i nazwisko" value={clientForm.name} onChange={(e) => setClientForm(f => ({ ...f, name: e.target.value }))} className="h-11 rounded-xl" />
            <div className="flex gap-2">
              <Input data-testid="quick-client-nip" placeholder="NIP" value={clientForm.nip} onChange={(e) => setClientForm(f => ({ ...f, nip: e.target.value }))} className="h-11 rounded-xl flex-1" />
              <NipLookupButton 
                nip={clientForm.nip} 
                onResult={(data) => {
                  setClientForm({
                    ...clientForm,
                    name: data.name || clientForm.name,
                    nip: data.nip?.replace(/\D/g, '') || clientForm.nip,
                    regon: data.regon || clientForm.regon,
                    address: data.address || clientForm.address,
                  });
                }}
                className="h-11"
              />
            </div>
            <Input data-testid="quick-client-email" placeholder="E-mail" value={clientForm.primaryEmail} onChange={(e) => setClientForm(f => ({ ...f, primaryEmail: e.target.value }))} className="h-11 rounded-xl" />
            <Input data-testid="quick-client-regon" placeholder="REGON (opc.)" value={clientForm.regon} onChange={(e) => setClientForm(f => ({ ...f, regon: e.target.value }))} className="h-11 rounded-xl" />
            <Input data-testid="quick-client-tags" placeholder="Tagi (po przecinku)" value={clientForm.tags} onChange={(e) => setClientForm(f => ({ ...f, tags: e.target.value }))} className="h-11 rounded-xl" />
            <Select value={clientForm.status} onValueChange={(v) => setClientForm(f => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="quick-client-status-trigger" className="h-11 rounded-xl">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="LEAD" data-testid="quick-client-status-lead">Lead</SelectItem>
                <SelectItem value="ACTIVE" data-testid="quick-client-status-active">Aktywny</SelectItem>
                <SelectItem value="PAUSED" data-testid="quick-client-status-paused">Wstrzymany</SelectItem>
                <SelectItem value="CLOSED" data-testid="quick-client-status-closed">Zakończony</SelectItem>
              </SelectContent>
            </Select>
            <Input data-testid="quick-client-phone" placeholder="Telefon (opc.)" value={clientForm.phone} onChange={(e) => setClientForm(f => ({ ...f, phone: e.target.value }))} className="h-11 rounded-xl" />
            <Input data-testid="quick-client-address" placeholder="Adres (opc.)" value={clientForm.address} onChange={(e) => setClientForm(f => ({ ...f, address: e.target.value }))} className="h-11 rounded-xl md:col-span-2" />
          </div>

          <Textarea data-testid="quick-client-notes" placeholder="Notatki (DLP)" value={clientForm.notes} onChange={(e) => setClientForm(f => ({ ...f, notes: e.target.value }))} className="min-h-[110px] rounded-xl" />

          <div className="flex justify-end gap-2">
            <Button data-testid="quick-client-cancel" variant="secondary" className="rounded-full" onClick={close}>Anuluj</Button>
            <Button data-testid="quick-client-submit" className="rounded-full bg-[#0A1C3A] hover:bg-[#07152d]" onClick={submitClient} disabled={!canCreateClient || loading}>
              {loading ? "Zapisywanie…" : "Dodaj"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={openKey === "task"} onOpenChange={(o) => setOpenKey(o ? "task" : "")}> 
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quick-task-dialog">
          <DialogHeader>
            <DialogTitle data-testid="quick-task-title">Nowe zadanie</DialogTitle>
          </DialogHeader>
          {!canCreateClient ? (
            <Alert data-testid="quick-task-no-access">
              <AlertTitle>Brak dostępu</AlertTitle>
              <AlertDescription>Tworzenie zadań jest dostępne dla Administratora i Menedżera.</AlertDescription>
            </Alert>
          ) : null}
          {error ? (
            <Alert data-testid="quick-task-error" variant="destructive">
              <AlertTitle>Błąd</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="grid gap-3 md:grid-cols-2">
            {/* Client search combobox */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  data-testid="quick-task-client-trigger"
                  className="h-11 rounded-xl justify-between w-full font-normal"
                >
                  <div className="flex items-center gap-2 truncate">
                    <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    {taskForm.clientId 
                      ? clients.find(c => c.id === taskForm.clientId)?.name || "Wybierz klienta"
                      : "Wybierz klienta"}
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Szukaj klienta..." className="h-10" />
                  <CommandList>
                    <CommandEmpty>Nie znaleziono klienta</CommandEmpty>
                    <CommandGroup>
                      {clients.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={c.name}
                          onSelect={() => setTaskForm(f => ({ ...f, clientId: c.id }))}
                          data-testid={`quick-task-client-${c.id}`}
                        >
                          <Check
                            className={cn("mr-2 h-4 w-4", taskForm.clientId === c.id ? "opacity-100" : "opacity-0")}
                          />
                          <div className="flex flex-col">
                            <span>{c.name}</span>
                            {c.nip && <span className="text-xs text-slate-400">NIP: {c.nip}</span>}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {/* User search combobox */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  data-testid="quick-task-assignee-trigger"
                  className="h-11 rounded-xl justify-between w-full font-normal"
                >
                  <div className="flex items-center gap-2 truncate">
                    <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    {taskForm.assigneeUserIds.length > 0 
                      ? `Przypisano: ${taskForm.assigneeUserIds.length} osób`
                      : "Przypisz do osób"}
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Szukaj użytkownika..." className="h-10" />
                  <CommandList>
                    <CommandEmpty>Nie znaleziono użytkownika</CommandEmpty>
                    <CommandGroup>
                      {allUsers.map((u) => (
                        <CommandItem
                          key={u.id}
                          value={`${u.fullName || ""} ${u.email}`}
                          onSelect={() => setTaskForm(f => {
                            const ids = f.assigneeUserIds.includes(u.id)
                              ? f.assigneeUserIds.filter(id => id !== u.id)
                              : [...f.assigneeUserIds, u.id];
                            return { ...f, assigneeUserIds: ids, assigneeUserId: ids[0] || "" };
                          })}
                          data-testid={`quick-task-assignee-${u.id}`}
                        >
                          <Check
                            className={cn("mr-2 h-4 w-4", taskForm.assigneeUserIds.includes(u.id) ? "opacity-100" : "opacity-0")}
                          />
                          <div className="flex flex-col">
                            <span>{u.fullName || u.email}</span>
                            <span className="text-xs text-slate-400">
                              {u.role === "ADMIN" ? "Administrator" : u.role === "MANAGER" ? "Menedżer" : "Pracownik"}
                              {u.fullName && ` • ${u.email}`}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {taskForm.assigneeUserIds.length > 0 && (
              <div className="flex flex-wrap gap-1 md:col-span-2">
                {taskForm.assigneeUserIds.map(id => {
                  const u = allUsers.find(u => u.id === id);
                  return (
                    <Badge key={id} variant="secondary" className="text-xs gap-1">
                      {u?.fullName || u?.email || id}
                      <button type="button" className="hover:text-red-500" onClick={() => setTaskForm(f => ({ ...f, assigneeUserIds: f.assigneeUserIds.filter(a => a !== id), assigneeUserId: f.assigneeUserIds.filter(a => a !== id)[0] || "" }))}>×</button>
                    </Badge>
                  );
                })}
              </div>
            )}

            <Input data-testid="quick-task-title-input" placeholder="Tytuł zadania" value={taskForm.title} onChange={(e) => setTaskForm(f => ({ ...f, title: e.target.value }))} className="h-11 rounded-xl md:col-span-2" />
            
            <Select value={taskForm.priority} onValueChange={(v) => setTaskForm(f => ({ ...f, priority: v }))}>
              <SelectTrigger data-testid="quick-task-priority-trigger" className="h-11 rounded-xl">
                <SelectValue placeholder="Priorytet" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CRITICAL" data-testid="quick-task-priority-critical">
                  🔴 Krytyczny
                </SelectItem>
                <SelectItem value="HIGH" data-testid="quick-task-priority-high">
                  🟠 Wysoki
                </SelectItem>
                <SelectItem value="MEDIUM" data-testid="quick-task-priority-medium">
                  🟡 Średni
                </SelectItem>
                <SelectItem value="LOW" data-testid="quick-task-priority-low">
                  🟢 Niski
                </SelectItem>
              </SelectContent>
            </Select>

            <div>
              <label className="text-xs text-slate-500">Termin wykonania</label>
              <Input 
                type="date" 
                data-testid="quick-task-due-date" 
                value={taskForm.dueDate} 
                onChange={(e) => setTaskForm(f => ({ ...f, dueDate: e.target.value }))} 
                className="h-11 rounded-xl" 
              />
            </div>
          </div>

          <Textarea data-testid="quick-task-description" placeholder="Opis zadania (opcjonalne)" value={taskForm.description} onChange={(e) => setTaskForm(f => ({ ...f, description: e.target.value }))} className="min-h-[80px] rounded-xl" />

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <Paperclip className="h-3.5 w-3.5" /> Zalaczniki ({taskFiles.length})
              </span>
              <label className="cursor-pointer text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                <Paperclip className="h-3 w-3" /> Dodaj pliki
                <input
                  type="file"
                  multiple
                  className="hidden"
                  data-testid="quick-task-files"
                  onChange={(e) => setTaskFiles(prev => [...prev, ...Array.from(e.target.files)])}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.txt,.zip,.pptx"
                />
              </label>
            </div>
            {taskFiles.length > 0 && (
              <div className="space-y-1">
                {taskFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-slate-50 border border-slate-100 text-xs">
                    <FileText className="h-3 w-3 text-slate-400 shrink-0" />
                    <span className="truncate flex-1">{f.name}</span>
                    <span className="text-slate-400 shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                    <button type="button" onClick={() => setTaskFiles(prev => prev.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button data-testid="quick-task-cancel" variant="secondary" className="rounded-full" onClick={close}>Anuluj</Button>
            <Button data-testid="quick-task-submit" className="rounded-full bg-[#0A1C3A] hover:bg-[#07152d]" onClick={submitTask} disabled={!canCreateClient || loading}>
              {loading ? "Zapisywanie…" : "Utwórz"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={openKey === "invoice"} onOpenChange={(o) => setOpenKey(o ? "invoice" : "")}> 
        <DialogContent className="rounded-2xl max-w-2xl h-[95vh] flex flex-col p-0 overflow-hidden" data-testid="quick-invoice-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0 border-b">
            <DialogTitle data-testid="quick-invoice-title">Wystaw fakturę (szkic)</DialogTitle>
          </DialogHeader>
          
          <div className="px-6 py-4 flex-1 overflow-y-scroll" style={{overflowY: 'scroll', WebkitOverflowScrolling: 'touch'}}>
            {!canCreateInvoice ? (
              <Alert data-testid="quick-invoice-no-access">
                <AlertTitle>Brak dostępu</AlertTitle>
                <AlertDescription>Faktury są dostępne tylko dla Administratora.</AlertDescription>
              </Alert>
            ) : null}
            {error ? (
              <Alert data-testid="quick-invoice-error" variant="destructive">
                <AlertTitle>Błąd</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

          <div className="grid gap-3 md:grid-cols-2">
            <Select value={invoiceForm.issuerId} onValueChange={(v) => setInvoiceForm(f => ({ ...f, issuerId: v }))}>
              <SelectTrigger data-testid="quick-invoice-issuer-trigger" className="h-11 rounded-xl">
                <SelectValue placeholder="Podmiot" />
              </SelectTrigger>
              <SelectContent>
                {issuers.map((i) => (
                  <SelectItem key={i.id} value={i.id} data-testid={`quick-invoice-issuer-${i.id}`}>{i.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={invoiceForm.clientId} onValueChange={(v) => setInvoiceForm(f => ({ ...f, clientId: v }))}>
              <SelectTrigger data-testid="quick-invoice-client-trigger" className="h-11 rounded-xl">
                <SelectValue placeholder="Klient" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`quick-invoice-client-${c.id}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input data-testid="quick-invoice-item" placeholder="Nazwa usługi" value={invoiceForm.itemName} onChange={(e) => setInvoiceForm(f => ({ ...f, itemName: e.target.value }))} className="h-11 rounded-xl md:col-span-2" />

            <Input data-testid="quick-invoice-qty" type="number" placeholder="Ilość" value={invoiceForm.qty} onChange={(e) => setInvoiceForm(f => ({ ...f, qty: Number(e.target.value) }))} className="h-11 rounded-xl" />
            <Input data-testid="quick-invoice-price" type="number" placeholder="Cena netto" value={invoiceForm.unitPriceNet} onChange={(e) => setInvoiceForm(f => ({ ...f, unitPriceNet: Number(e.target.value) }))} className="h-11 rounded-xl" />
            <Input data-testid="quick-invoice-vat" type="number" placeholder="VAT %" value={invoiceForm.vatRate} onChange={(e) => setInvoiceForm(f => ({ ...f, vatRate: Number(e.target.value) }))} className="h-11 rounded-xl" />
            <Input data-testid="quick-invoice-paydays" type="number" placeholder="Termin (dni)" value={invoiceForm.paymentDays} onChange={(e) => setInvoiceForm(f => ({ ...f, paymentDays: Number(e.target.value) }))} className="h-11 rounded-xl" />
          </div>

          </div>

          <div className="px-6 py-4 border-t flex-shrink-0 flex justify-end gap-2">
            <Button data-testid="quick-invoice-cancel" variant="secondary" className="rounded-full" onClick={close}>Anuluj</Button>
            <Button data-testid="quick-invoice-submit" className="rounded-full bg-[#E07A13] hover:bg-[#c96810]" onClick={submitInvoice} disabled={!canCreateInvoice || loading}>
              {loading ? "Zapisywanie…" : "Zapisz szkic"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
